import contentRequest from '~/src/api/requests/contentRequest';
import { IShopCategory } from '~/src/api/content/shop/getShopCategories';
import { captureException } from '@sentry/node';
import { EPriceTypes } from '~/src/models/price';
import { getAccessModeByRights } from '~/src/helpers/accessMode';

export enum EShopCategory {
    Antiviruses = 'antiviruses',
    SpeedBonuses = 'speed-bonuses',
    Routers = 'routers',
    Intercom = 'intercom',
    Sale = 'special-offers',
    PartnerServices = 'partner-service-with-phone-check',
    Decoders = 'decoders',
    BonusToAll = 'bonus-to-all',
    YandexStation = 'yandex-stanciya',
    SafeKids = 'safe-kids',
    LoyaltyProgram = 'loyalty-program',
}

export enum EExpandShopItems {
    Category = 'category',
    Props = 'props',
    Labels = 'labels',
}

interface IFilter {
    key: string | [],
    value: string | number,
}

export interface IShopLabel {
    id: number,
    name: string,
    title: string,
    color: string,
}

export interface IShopProps {
    name: string,
    type: string,
    value: string | string[],
}

export interface ICostRa {
    dur_type_id: number,
    duration: number,
    flag_ra: number,
}

export interface IShopPriceOptions {
    label: string,
    price: number,
    pricePromo?: number,
    type: EPriceTypes.Possess | EPriceTypes.Leasing,
    operType: string,
}

export interface IShopItem {
    alias: string,
    body: string,
    body_full: string,
    button_link: string,
    button_text: string,
    category_id: number,
    exists: boolean,
    extra: unknown[],
    global_id: number,
    id: number,
    image?: string,
    images: string[],
    costRa: ICostRa[],
    // 0 или 1
    is_sale: number,
    prices: unknown,
    pricesPromo: unknown,
    sort: null | number,
    title: string,
    labels?: IShopLabel[],
    category?: IShopCategory,
    props?: IShopProps[],
    priceOptions?: IShopPriceOptions[],
}

interface IShopItemParams {
    expand?: string | undefined,
    filter?: IFilter[] | undefined,
    category?: EShopCategory | string | null,
    accessMode?: number,
}

export async function getShopItems(
    domain: string,
    token: string | null,
    category: EShopCategory | string | null,
    expand?: EExpandShopItems[],
    filter?: IFilter[]
): Promise<IShopItem[]> {
    const params: IShopItemParams = {
        expand: prepareExpandProp(expand),
        filter,
    };

    const headers = token
        ? { Authorization: `Bearer ${token}` }
        : {};

    if (category) {
        params.category = category;
    }

    params.accessMode = getAccessModeByRights({
        isAuth: Boolean(token),
    });

    try {
        const resp = await contentRequest.get(`${domain}/v1/shop/items`, {
            headers,
            params,
            // TODO: заменить на qs.stringify и ничего не поломать
            paramsSerializer: (params: any) => {
                return Object.keys(params)
                    .map(key => {
                        if (Array.isArray(params[key])) {
                            return params[key].map((item: IFilter) => {
                                if ('string' === typeof item['key']) {
                                    return encodeURIComponent(key) +
                                        '[' + encodeURIComponent(item['key']) + ']' +
                                        '=' +
                                        encodeURIComponent(item['value']);
                                } else {
                                    for (const subKey in item['key']) {
                                        return encodeURIComponent(key) +
                                            '[' + encodeURIComponent(subKey) + ']' +
                                            '[' + encodeURIComponent(item['key'][subKey]) + ']' +
                                            '=' +
                                            encodeURIComponent(item['value']);
                                    }
                                }
                            }).join('&');
                        } else if (undefined === params[key]) {
                            return undefined;
                        } else {
                            return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
                        }
                    })
                    .filter(item => undefined !== item)
                    .join('&');
            }
        });

        return resp.data.items;

    } catch (e) {
        captureException(e);
        return [];
    }
}

export async function getBonusToAll(domain: string, token: string | null, expand?: EExpandShopItems[]): Promise<IShopItem[]> {
    let expandDefault = [EExpandShopItems.Category];
    if (expand) {
        expandDefault = [...expandDefault, ...expand];
    }
    return await getShopItems(domain, token, EShopCategory.BonusToAll, expandDefault);
}

function prepareExpandProp(expands: EExpandShopItems[] | undefined): string | undefined {
    if (expands && expands.length) {
        return expands.join(',');
    }
    return undefined;
}

// eslint-disable-next-line no-undef
export async function getAntivirusesAuth(domain: string, token: string): Promise<unknown> {
    return getShopItems(
        domain,
        token,
        EShopCategory.Antiviruses,
        [EExpandShopItems.Category, EExpandShopItems.Props]
    );
}

// eslint-disable-next-line no-undef
export async function getSpeedBonuses(domain: string, token: string): Promise<unknown> {
    return getShopItems(
        domain,
        token,
        EShopCategory.SpeedBonuses,
        [EExpandShopItems.Category]
    );
}

// eslint-disable-next-line no-undef
export async function getRouters(domain: string, token: string): Promise<unknown> {
    return getShopItems(
        domain,
        token,
        EShopCategory.Routers,
        [EExpandShopItems.Category, EExpandShopItems.Props, EExpandShopItems.Labels]
    );
}

// eslint-disable-next-line no-undef
export async function getIntercomServices(domain: string, token: string): Promise<unknown> {
    return getShopItems(
        domain,
        token,
        EShopCategory.Intercom,
        [EExpandShopItems.Category, EExpandShopItems.Props]
    );
}

// eslint-disable-next-line no-undef
export async function getSale(domain: string, token: string): Promise<unknown> {
    const filter: IFilter[] = [
        { 'key': 'is_sale', 'value': 1 },
    ];

    return getShopItems(
        domain,
        token,
        EShopCategory.Sale,
        [EExpandShopItems.Category, EExpandShopItems.Props],
        filter
    );
}

// eslint-disable-next-line no-undef
export async function getSafeKids(domain: string, token: string): Promise<IShopItem[]> {
    return getShopItems(
        domain,
        token,
        EShopCategory.SafeKids,
        [EExpandShopItems.Category, EExpandShopItems.Props]
    );
}

// eslint-disable-next-line no-undef
export async function getLoyaltyPartners(domain: string, token: string): Promise<IShopItem[]> {
    return getShopItems(
        domain,
        token,
        EShopCategory.LoyaltyProgram,
        [EExpandShopItems.Category],
    );
}
